import { RendererType } from '../../../../../data-models/field-definition';
import { IField } from '../../../../../data-models/field2.data-model';
import {
  ICustomNumberFormatConfig,
  IFormatterDataModel,
} from '../../../../../data-models/formatter.data-model';
import { FMT, StandardFormatterId } from '../../../../../util/formatter-service';
import { DateFormattersConfig } from '../../../../../util/formatters/DateFormatters';
import { NumericFormattersConfig } from '../../../../../util/formatters/NumericFormatters';
import { StringFormattersConfig } from '../../../../../util/formatters/StringFormatters';

/**
 * Single source of truth for which formatter model a KPI uses in the Financials grid.
 * Used by CompanyProfileFinancialsTable and the KPI Display admin config
 * to show the correct default in the formatter dropdown.
 */
export function getCurrencyAwareFormatterModel(
  kpi: IField<unknown>,
  currency?: string | null
): IFormatterDataModel<unknown> {
  const rendererType = kpi.formMeta?.renderer?.type;

  switch (rendererType) {
    // Currency is not stored on kpiConfig; it comes from the financials grid's
    // selected FX currency (FD-3875), else the base currency formatter.
    case RendererType.currency:
      return currencyFormatterModel(currency);
    case RendererType.integer:
      return NumericFormattersConfig.integer;
    case RendererType.multiplier:
      return NumericFormattersConfig.multiplier;
    case RendererType.number:
    case RendererType.numeric:
      return NumericFormattersConfig.numeric;
    case RendererType.percent:
      return NumericFormattersConfig.percent;
    case RendererType.date:
      return DateFormattersConfig.date;
    default:
      return StringFormattersConfig.string;
  }
}

/**
 * Currency formatter model for a currency KPI. Derives from the registered
 * `currency` model (read from base meta, which the sweep never touches) so
 * client formatter customizations are preserved.
 *
 * - No currency selected (e.g. the admin KPI Display config): return the
 *   registered `currency` model so its id matches the formatter dropdown options.
 * - A currency selected (the financials grid always passes one): return a
 *   `financials-currency:<code>` id that does NOT start with `currency`/`usd`,
 *   so the app-level currency toggle's formatter sweep (updateCurrencyFormatters
 *   in useSelectedCurrency) can't clobber it.
 */
function currencyFormatterModel(currency?: string | null): IFormatterDataModel<unknown> {
  const base = (FMT.get().getFormatterModel('currency' as StandardFormatterId) ??
    NumericFormattersConfig.currency) as IFormatterDataModel<ICustomNumberFormatConfig>;
  if (!currency) {
    return base;
  }
  const code = currency.toUpperCase();
  return {
    ...base,
    id: `financials-currency:${code}`,
    config: { ...base.config, currency: code },
  };
}
